import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import type { Media } from '../../api/types'
import { useTexte } from '../../texte'
import { Medienkachel } from '../../components/Medienkachel'
import { rueckfrage } from '../../components/Rueckfrage'
import { XIcon } from '../../components/Icons'
import { Restzeichen } from '../../components/Restzeichen'
import { Auszeichnungsknoepfe } from '../../components/Auszeichnungsknoepfe'
import { useEscape } from '../../hooks/useEscape'
import { useSchreibfeld } from '../../hooks/useSchreibfeld'
import { schreibe } from '../../lib/reichtext'

/**
 * Change a post -- as a sheet, not inside the bubble (1.68.0).
 *
 * From use, with a screenshot: "wenn längere Texte im Chat zur Änderung
 * aufgerufen werden, ist das Fenster sehr klein und man kann kaum navigieren.
 * Die Kopfleiste verschwindet ganz - wird nach oben geschoben; großer Abstand
 * zwischen der Eingabezeile und der Tastatur; (braucht man die Eingabezeile
 * überhaupt im Änderungsmodus?)"
 *
 * **Four findings, and they had one cause between them.** The field used to
 * sit in the bubble, and the bubble sits in the timeline -- a scroll box. To
 * free a field in there, iOS shifts the **whole page** up, and that is the
 * header that went missing. The composer stayed where it was because it knew
 * nothing of the change, so two writing fields stood there at once and the
 * wrong one took the room.
 *
 * A sheet has none of that, and not because it was built for this: `.bogen`
 * is `position: fixed`, it ends at the **visible** edge (`--tastatur-hoehe`)
 * and it reserves the strip for iOS' own bar. That last one is #44, reported
 * back then in the same words -- "Abstand zwischen Formular und Tastatur" --
 * and solved for the highlight form. The change to a post simply was not a
 * sheet.
 *
 * **The answer to the question is no.** While somebody changes a post, the
 * composer is behind the veil and out of the way.
 *
 * Handle, escape and veil stand **here** and not at the caller (1.63.0) --
 * the rule that `Wetterwahl` broke. The veil takes no taps: `.bogen` has
 * `pointer-events: none` so a map underneath stays draggable, and that is
 * shared by all thirteen sheets. Closing goes through the X, `Abbrechen` and
 * Escape, exactly as at the neighbours.
 *
 * **And the post's pictures, with the cross** (1.88.0, from use: "im Chat:
 * ein Bild aus der Galerie entfernen. im Highlight geht das ja schon"). The
 * same tile as in the point's form (`Medienkachel`), but the cross here only
 * *marks*: a picture leaves with "Speichern", after one question, and
 * "Abbrechen" takes every mark back. The point's form deletes at the cross
 * because it has no other moment; this sheet has one, and a post that
 * others have already read should not change under a slipped finger. What
 * happens to the file is the server's rule (`MessagesController::edit()`):
 * gone only where it hangs nowhere else.
 *
 * The sheet carries no modifier of its own: `.bogen` already is everything
 * it needs. What a test run holds on to is `.aenderung__feld` -- a class
 * that carries rules, not a word from the surface.
 */
export function Aenderungsblatt({
  wortlaut,
  bilder = [],
  bleibtSonst = false,
  grenze,
  laeuft,
  fehler,
  onSichern,
  onZu,
}: {
  wortlaut: string
  /** Pictures and videos of the post; a recording is not a picture and stays. */
  bilder?: Media[]
  /** Something else stays on the post whatever happens here: a separator, a card, a recording. */
  bleibtSonst?: boolean
  grenze: number
  laeuft: boolean
  fehler: string | null
  /** The text, and the ids of the pictures that leave the post. */
  onSichern: (text: string, weg: number[]) => void
  onZu: () => void
}) {
  const t = useTexte()
  const [entwurf, setEntwurf] = useState(wortlaut)
  const [weg, setWeg] = useState<number[]>([])
  const bleibend = bilder.filter((bild) => !weg.includes(bild.id))
  // The server's rule, mirrored so the button says it before the server
  // does: a post of nothing is a deleted post, and that has its own entry.
  const nichtsUebrig = entwurf.trim() === '' && bleibend.length === 0 && !bleibtSonst

  async function sichern() {
    if (weg.length > 0 && !(await rueckfrage(t.chat.aenderungBilderFrage(weg.length), t.chat.aenderungBilderKnopf))) {
      return
    }
    onSichern(entwurf, weg)
  }

  /*
   * **Dasselbe Schreibfeld wie in der Eingabezeile** (1.69.0). Aus dem
   * Betrieb: „im Übrigen sollte das auch im Änderungsmodus funktionieren."
   *
   * Hier stand ein `<textarea>`, und der kann genau eine Schrift -- fett sähe
   * darin aus wie mager, und die gespeicherten Sternchen stünden als das da,
   * was sie sind: Zeichen. Wer einen ausgezeichneten Beitrag änderte, sah
   * `*so*` statt **so**.
   */
  const schreibfeld = useSchreibfeld(setEntwurf)
  // In Zeichen gezählt, wie im `Restzeichen` daneben und wie in der
  // Eingabezeile -- der Server prüft zusätzlich Bytes (`Textgrenzen`).
  const zuLang = [...entwurf.trim()].length > grenze

  useEscape(onZu)

  /*
   * **Der Cursor steht am Ende und nicht am Anfang.** Wer etwas ändert, hängt
   * meistens etwas an; stünde er vorn, müsste sich jeder erst durch den
   * eigenen Text nach unten arbeiten. Auf dem Bildschirmfoto aus dem Betrieb
   * steht er vor dem ersten Wort -- das ist der alte Weg.
   */
  useEffect(() => {
    const el = schreibfeld.feld.current
    if (el === null) return
    schreibe(el, wortlaut)
    el.focus()
    // Ans Ende, nicht an den Anfang -- in einem `contenteditable` geht das
    // über einen Bereich und nicht über `setSelectionRange`.
    const bereich = document.createRange()
    bereich.selectNodeContents(el)
    bereich.collapse(false)
    const auswahl = window.getSelection()
    auswahl?.removeAllRanges()
    auswahl?.addRange(bereich)
    // Einmal beim Aufmachen, mit dem Wortlaut aus der Nachricht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return createPortal(
    <div
      className="bogen"
      role="dialog"
      aria-modal="true"
      aria-label={t.chat.beitragAendern}
    >
      <div className="bogen__blatt bogen__blatt--voll bogen__blatt--gegliedert aenderung__blatt">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{t.chat.beitragAendern}</h3>
          <button
            type="button"
            className="icon-button"
            onClick={onZu}
            aria-label={t.allgemein.schliessen}
          >
            <XIcon size={20} />
          </button>
        </div>

        {/*
          * **Die Mitte ist hier keine `.bogen__rolle`**, und das ist derselbe
          * Fall wie bei der Routenwahl (1.61.0): dieses Feld ist schon ein
          * Scrollkasten. In einen zweiten gelegt gäbe es zwei ineinander, und
          * auf iOS entscheidet dann der Zufall, welcher den Schwung bekommt.
          *
          * **Und die Grenze hält jetzt der Knopf, nicht das Feld.** Am
          * `textarea` stand `maxLength`; ein `contenteditable` kennt das
          * nicht. Statt still zu kürzen -- was die Regel des Hauses ohnehin
          * verbietet -- steht der Zähler daneben und *Speichern* ist aus,
          * solange es zu lang ist. Dieselbe Bauart wie in der Eingabezeile.
          */}
        <div
          ref={schreibfeld.feld}
          className="field__input aenderung__feld"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={t.chat.beitragAendern}
          onInput={schreibfeld.onInput}
          onPaste={schreibfeld.onPaste}
          onKeyDown={(event) => {
            // Enter macht hier einen Absatz und sendet **nicht**: im Blatt
            // steht ein langer Text, und ein Absatz ist darin der Normalfall
            // -- gespeichert wird über den Knopf, der dasteht.
            if (event.key === 'Escape') onZu()
          }}
        />

        <div className="aenderung__zeile">
          {/* **Die Reihe steht hier fest und verschwindet nicht mit der
              Markierung** -- anders als in der Eingabezeile, und aus demselben
              Grund: Platz. Dort ist keiner, hier ist welcher. Eine Reihe, die
              beim Markieren auftaucht, verschöbe das Feld unter dem Finger,
              und genau darauf zielt der Finger gerade. Ohne Markierung stehen
              die Knöpfe grau da statt zu verschwinden. */}
          <span className="aenderung__marken">
            <Auszeichnungsknoepfe
              lege={schreibfeld.lege}
              aus={!schreibfeld.hatAuswahl}
              klasse="auszeichnen__knopf"
            />
          </span>
          <Restzeichen wert={entwurf} grenze={grenze} />
          {fehler !== null && (
            <span className="warnend klein" role="alert">
              {fehler}
            </span>
          )}
        </div>

        {bilder.length > 0 && (
          <div className="aenderung__bilder" data-schluessel="aenderung-bilder">
            <span className="field__label">{t.chat.aenderungBilder}</span>
            <div className="bogen__bilder">
              {bleibend.map((bild) => (
                <Medienkachel
                  key={bild.id}
                  media={bild}
                  bezeichnung={t.chat.aenderungBildEntfernen}
                  onWeg={() => setWeg((liste) => [...liste, bild.id])}
                />
              ))}
            </div>
          </div>
        )}
        {nichtsUebrig && (
          <p className="warnend klein" role="alert" data-schluessel="aenderung-leer">
            {t.chat.aenderungLeer}
          </p>
        )}

        <div className="bogen__fuss">
          <button
            type="button"
            className="button"
            disabled={laeuft || zuLang || nichtsUebrig}
            onClick={() => void sichern()}
          >
            {laeuft ? t.allgemein.laeuft : t.allgemein.speichern}
          </button>
          <button type="button" className="button button--outline" onClick={onZu}>
            {t.allgemein.abbrechen}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
